import { useState, useCallback } from 'react';
import {
    Button,
    Card,
    Center,
    Stack,
    Text,
    Title,
    ThemeIcon,
    Alert,
} from '@mantine/core';
import { Icon } from '@iconify/react';
import { verifyEmail } from 'supertokens-web-js/recipe/emailverification';
import { notifications } from '@mantine/notifications';

// This is the screen that is shown when the user clicks on the verification link in the email.
// We have to make them manually click the button to verify their email so that email scanners
// don't scan the link and trigger the verification process automatically.

type VerificationStatus = 'idle' | 'loading' | 'success' | 'expired' | 'error';

export default function VerifyEmail() {

    const [status, setStatus] = useState<VerificationStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleVerifyEmail = useCallback(async () => {

        setStatus('loading');
        setErrorMessage(null);

        try {
            const response = await verifyEmail();

            if (response.status === 'EMAIL_VERIFICATION_INVALID_TOKEN_ERROR') {
                setStatus('expired');
                setErrorMessage('The verification link has expired or is invalid.');
            } else {
                setStatus('success');
                
                notifications.show({
                    title: 'Success!',
                    message: 'Email verified successfully.',
                    color: 'green',
                });

                // Redirect to home after a brief delay
                setTimeout(() => {
                    window.location.assign('/');
                }, 2000);
            }
        } catch (err: unknown) {
            setStatus('error');
            const message = err instanceof Error ? err.message : 'An unexpected error occurred';
            setErrorMessage(message);
            
            notifications.show({
                title: 'Oops! Something went wrong.',
                message: message,
                color: 'red',
            });
        }
        
    }, []);

    const handleGoToResend = useCallback(() => {
        window.location.assign('/verify-email');
    }, []);

    const getIconConfig = () => {
        switch (status) {
            case 'success':
                return { icon: 'mdi:check-circle', color: 'green' };
            case 'expired':
                return { icon: 'mdi:clock-alert', color: 'orange' };
            case 'error':
                return { icon: 'mdi:alert-circle', color: 'red' };
            default:
                return { icon: 'mdi:shield-check', color: 'blue' };
        }
    };

    const getTitle = () => {
        switch (status) {
            case 'success':
                return 'Email Verified!';
            case 'expired':
                return 'Link Expired';
            case 'error':
                return 'Verification Failed';
            case 'loading':
                return 'Verifying...';
            default:
                return 'Complete Verification';
        }
    };

    const { icon, color } = getIconConfig();

    return (
        <Center pt={100} p="md">
            <Card withBorder shadow="md" p="xl" radius="md" w={450}>
                <Stack gap="lg" align="center">
                    {/* Icon */}
                    <ThemeIcon
                        size={80}
                        radius="xl"
                        variant="light"
                        color={color}
                    >
                        <Icon icon={icon} width={40} height={40} />
                    </ThemeIcon>

                    {/* Title */}
                    <Title order={2} ta="center">
                        {getTitle()}
                    </Title>

                    {/* Description */}
                    <Text c="dimmed" ta="center" size="sm">
                        {status === 'success' ? (
                            <>
                                Your email has been verified successfully.
                                <br />
                                Redirecting you to the app...
                            </>
                        ) : status === 'expired' ? (
                            <>
                                This verification link has expired or has already been used.
                                <br />
                                Please request a new verification email.
                            </>
                        ) : status === 'error' ? (
                            errorMessage
                        ) : status === 'loading' ? (
                            'Please wait while we verify your email address...'
                        ) : (
                            <>
                                You're almost there! Click the button below to
                                <br />
                                complete your email verification.
                            </>
                        )}
                    </Text>

                    {/* Expired Alert */}
                    {status === 'expired' && (
                        <Alert
                            color="orange"
                            variant="light"
                            w="100%"
                            icon={<Icon icon="mdi:information" width={18} />}
                        >
                            Verification links are valid for a limited time for security reasons.
                        </Alert>
                    )}

                    {/* Error Alert */}
                    {status === 'error' && (
                        <Alert
                            color="red"
                            variant="light"
                            w="100%"
                            icon={<Icon icon="mdi:alert" width={18} />}
                        >
                            Unable to verify your email. Please try again or request a new link.
                        </Alert>
                    )}

                    {/* Actions */}
                    <Stack gap="sm" w="100%" mt="md">
                        {status === 'success' ? (
                            <Button
                                fullWidth
                                color="green"
                                leftSection={<Icon icon="mdi:check" width={18} />}
                                onClick={() => window.location.assign('/')}
                            >
                                Continue to App
                            </Button>
                        ) : status === 'expired' ? (
                            <Button
                                fullWidth
                                leftSection={<Icon icon="mdi:email-fast" width={18} />}
                                onClick={handleGoToResend}
                            >
                                Request New Verification Email
                            </Button>
                        ) : status === 'error' ? (
                            <>
                                <Button
                                    fullWidth
                                    leftSection={<Icon icon="mdi:refresh" width={18} />}
                                    onClick={handleVerifyEmail}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    fullWidth
                                    variant="light"
                                    leftSection={<Icon icon="mdi:email-fast" width={18} />}
                                    onClick={handleGoToResend}
                                >
                                    Request New Link
                                </Button>
                            </>
                        ) : (
                            <Button
                                fullWidth
                                loading={status === 'loading'}
                                leftSection={status !== 'loading' ? <Icon icon="mdi:shield-check" width={18} /> : undefined}
                                onClick={handleVerifyEmail}
                            >
                                Verify My Email
                            </Button>
                        )}
                    </Stack>

                    {/* Security Note */}
                    {status === 'idle' && (
                        <Text size="xs" c="dimmed" ta="center" mt="sm">
                            <Icon icon="mdi:information-outline" width={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            For security, we require a manual click to prevent automated scanners from verifying emails.
                        </Text>
                    )}
                </Stack>
            </Card>
        </Center>
    );
}
